import { WebComponent } from '@cian/mf-registry/browser';
import * as React from 'react';
import { hydrateRoot, createRoot } from 'react-dom/client';

import { Application } from '../../shared/containers/App';
import { IApplicationContext } from '../../shared/types/applicationContext';
import { TReduxStore } from '../../shared/types/redux';
import { activateHeaderUIAPIController } from '../services/headerUIAPIController/bridge';

export function createCustomElementConstructor(
  context: IApplicationContext,
  store: TReduxStore,
  apiController: Parameters<typeof activateHeaderUIAPIController>[0],
): CustomElementConstructor {
  class Header extends WebComponent {
    private applicationContext: IApplicationContext;
    private disposeAPIController: (() => void) | undefined;

    protected microfrontendName = context.config.getStrict<string>('projectName');
    protected imageVersion = '';
    protected runtimeName = '/v3/get-header-microfrontend/';

    public async connectedCallback(): Promise<void> {
      const instanceId = this.getAttribute('instance') || undefined;
      this.applicationContext = { ...context, config: context.config.attach({ instanceId }) };
      this.imageVersion = this.applicationContext.config.getStrict<string>('version.commit');
      const connected = super.connectedCallback();

      this.disposeAPIController?.();
      const disposeAPIController = activateHeaderUIAPIController(apiController);
      this.disposeAPIController = disposeAPIController;

      try {
        await connected;
      } catch (error) {
        if (this.disposeAPIController === disposeAPIController) {
          disposeAPIController();
          this.disposeAPIController = undefined;
        }
        throw error;
      }
    }

    public disconnectedCallback(): void {
      this.disposeAPIController?.();
      this.disposeAPIController = undefined;
      super.disconnectedCallback();
    }

    public async render() {
      const reactElement = <Application {...this.props} context={this.applicationContext} reduxStore={store} />;

      if (this.shouldHydrate) {
        hydrateRoot(this, reactElement);
      } else {
        createRoot(this).render(reactElement);
      }
    }
  }

  return Header;
}
